import * as PIXI from 'pixi.js';
import type { XY } from '../../utils/MathUtils';
import MathUtils from '../../utils/MathUtils';
import type { FXParticleEmitterConfig } from './FXParticleEmitterConfig';

export class FXParticle extends PIXI.Sprite {
  public posX = 0;

  public posY = 0;

  public velocity: XY;

  private allowTransforms = true;

  private pTime = 0;

  private pLife = 0;

  private targetAlpha = 1;

  private fadeRate = 1;

  private scaleMultiplier = 1;

  public rotationVelocity = 0;

  constructor() {
    super();

    this.posX = 0;
    this.posY = 0;
    this.velocity = { x: 0, y: 0 };
    this.anchor.set(0.5, 0.5);
  }

  public init(
    config: FXParticleEmitterConfig,
    getRandomNumber: () => number,
    baseAngle = 0,
    emitAtAngle?: number,
  ): void {
    this.allowTransforms = config.allowTransforms;

    const frame = config.keys[Math.floor(config.keys.length * getRandomNumber())];
    this.texture = PIXI.Texture.from(frame);

    this.posX = 0;
    this.posY = 0;
    this.blendMode = config.blendMode ?? 'normal';

    this.pTime = 0;
    this.pLife = MathUtils.getRandomValueInRange(config.minParticleLife, config.maxParticleLife, getRandomNumber);

    // set alpha / fading values
    this.alpha = config.initialAlpha;
    this.targetAlpha = config.targetAlpha;
    this.fadeRate = config.particleFadeRate;

    // velocity / emission angle
    const emitAngle =
      emitAtAngle ?? baseAngle + (config.emitAngleVariance - getRandomNumber() * 2 * config.emitAngleVariance);
    const velocityLength = config.minParticleVel + getRandomNumber() * (config.maxParticleVel - config.minParticleVel);
    MathUtils.getVectorFromAngle(emitAngle, this.velocity, velocityLength);

    // calculate emission offset if spawnOffsetRadius set
    if (config.spawnOffsetRadius > 0) this.applyOffsetRadius(config.spawnOffsetRadius, emitAngle);

    // apply spawn X/Y randomness
    if (config.spawnRandomX !== 0)
      this.posX += MathUtils.getRandomValueInRange(-config.spawnRandomX, config.spawnRandomX, getRandomNumber);
    if (config.spawnRandomY !== 0)
      this.posY += MathUtils.getRandomValueInRange(-config.spawnRandomY, config.spawnRandomY, getRandomNumber);

    if (this.allowTransforms) {
      // apply transforms if allowed
      const particleScale = this.allowTransforms
        ? MathUtils.getRandomValueInRange(config.minParticleScale, config.maxParticleScale, getRandomNumber)
        : 1;
      this.scale.set(particleScale);

      this.scaleMultiplier = this.allowTransforms ? config.scaleMultiplier : 1;
      this.rotationVelocity = MathUtils.getRandomValueInRange(
        config.minRotationVelocity,
        config.maxRotationVelocity,
        getRandomNumber,
      );

      if (config.orientToVector) {
        // particle rotation should be oriented to the current vector
        this.rotation = MathUtils.getVectorAngle(this.velocity.x, this.velocity.y) + config.orientToVectorOffset;
      } else {
        // rotation uses random rules
        this.rotation = MathUtils.getRandomValueInRange(
          config.minParticleRotation,
          config.maxParticleRotation,
          getRandomNumber,
        );
      }
    } else {
      this.resetTransform();
    }
  }

  public updateParticle(dtMs: number, dtSec: number): void {
    this.pTime += dtMs;
    if (this.pTime < 0) return;
    this.posX += dtSec * this.velocity.x;
    this.posY += dtSec * this.velocity.y;

    // fade in / out particle
    if (this.pTime > this.pLife) {
      this.alpha -= dtSec * this.fadeRate;
      if (this.alpha < 0) this.alpha = 0;
    } else if (this.alpha < this.targetAlpha) {
      this.alpha += dtSec * this.fadeRate;
      if (this.alpha > this.targetAlpha) this.alpha = this.targetAlpha;
    }

    if (this.allowTransforms) {
      // apply scale change
      if (this.scaleMultiplier !== 1) {
        const deltaScale = dtSec * (this.scaleMultiplier - 1);
        const newScale = Math.max(this.scale.x + deltaScale, 0);
        this.scale.set(newScale);
      }
      // update rotation
      if (this.rotationVelocity !== 0) {
        this.rotation += dtSec * this.rotationVelocity;
      }
    }
  }

  public isExpired(): boolean {
    return this.pTime > this.pLife && (this.alpha <= 0 || this.fadeRate === 0);
  }

  private applyOffsetRadius(radius: number, emitAngle: number): void {
    const pointOnCircle = MathUtils.getPointOnCircle(0, 0, radius, emitAngle);
    this.posX = pointOnCircle.x;
    this.posY = pointOnCircle.y;
  }

  private resetTransform(): void {
    this.alpha = 1;
    this.rotation = 0;
    this.scale.set(1);
    this.rotationVelocity = 0;
    this.scaleMultiplier = 1;
  }
}
